'use client';

import Check from 'lucide-react/dist/esm/icons/check';
import Copy from 'lucide-react/dist/esm/icons/copy';
import Facebook from 'lucide-react/dist/esm/icons/facebook';
import Share2 from 'lucide-react/dist/esm/icons/share-2';
import Twitter from 'lucide-react/dist/esm/icons/twitter';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

export default function Share() {
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    setShareUrl(window.location.href);
  }, []);

  function copyToClipboard() {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (!shareUrl) {
    return <div className="mb-2 h-[72px] w-full" aria-hidden="true" />;
  }

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: WhatsAppIcon,
      url: `https://wa.me/?text=${encodeURIComponent(shareUrl)}`,
      color: 'from-green-500 to-emerald-600',
      hoverColor: 'from-green-400 to-emerald-500',
    },
    {
      name: 'Telegram',
      icon: Share2,
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`,
      color: 'from-blue-500 to-cyan-600',
      hoverColor: 'from-blue-400 to-cyan-500',
    },
    {
      name: 'X (Twitter)',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`,
      color: 'from-slate-700 to-slate-800',
      hoverColor: 'from-slate-600 to-slate-700',
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      color: 'from-blue-600 to-indigo-700',
      hoverColor: 'from-blue-500 to-indigo-600',
    },
  ];

  const CopyIcon = copied ? Check : Copy;

  return (
    <div className="mb-2 w-full">
      <div className="share-card overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 shadow-lg shadow-slate-200/50 backdrop-blur-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 dark:shadow-slate-900/50">
        <div className="share-content p-3">
          {!copied ? (
            <div className="grid grid-cols-5 gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="copy-btn relative overflow-hidden border-0 bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md transition-all duration-300 hover:scale-105 hover:from-emerald-400 hover:to-teal-500 hover:shadow-lg"
                onClick={copyToClipboard}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 transition-opacity duration-300 hover:opacity-100"></div>
                <span className="relative z-10 flex items-center justify-center">
                  <CopyIcon className="h-4 w-4" />
                  <span className="sr-only">העתק קישור</span>
                </span>
              </Button>

              {shareOptions.map((option) => (
                <a
                  key={option.name}
                  href={option.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-option group"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="share-btn relative w-full overflow-hidden border border-slate-300 bg-gradient-to-br from-slate-100 to-slate-200 transition-all duration-300 hover:scale-105 hover:shadow-md dark:border-slate-600 dark:from-slate-800 dark:to-slate-700"
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${option.color} opacity-0 transition-opacity duration-300 group-hover:opacity-10`}
                    ></div>
                    <span className="relative z-10 flex items-center justify-center">
                      <option.icon className="h-4 w-4 text-slate-600 transition-colors duration-300 group-hover:text-slate-800 dark:text-slate-400 dark:group-hover:text-slate-200" />
                      <span className="sr-only">{option.name}</span>
                    </span>
                  </Button>
                </a>
              ))}
            </div>
          ) : (
            <div className="copy-success flex items-center gap-2 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 p-2 text-center animate-in fade-in slide-in-from-top-2 dark:border-emerald-700 dark:from-emerald-950/30 dark:to-green-950/30">
              <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                הועתק!
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
